import { inject, Signal } from '@angular/core';
import { AuthFacade, AuthorizationService } from '@iot-platform/auth';
import { FavoriteViewsFacade } from '@iot-platform/data-access/favorite-views';
import { GridEngineFacade, GridScrollStateService } from '@iot-platform/grid-engine';
import { ApiFeatureSelector, ApiSelector, BaseFacade, CommonGenericModel, FavoriteView } from '@iot-platform/models/common';
import { I4BGrid, I4BGridData, I4BGridOptions } from '@iot-platform/models/grid-engine';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

/* eslint-disable @angular-eslint/prefer-inject */
export abstract class I4BBaseFacade<T extends CommonGenericModel, P, F> extends BaseFacade<T, P, F> {
  protected readonly authFacade: AuthFacade = inject(AuthFacade);
  protected readonly authorizationService: AuthorizationService = inject(AuthorizationService);
  protected readonly gridScrollStateService: GridScrollStateService = inject(GridScrollStateService);
  protected readonly favoriteViewsFacade: FavoriteViewsFacade = inject(FavoriteViewsFacade);
  protected readonly gridEngineFacade: GridEngineFacade = inject(GridEngineFacade);

  abstract concept: string;

  selectedItemInGrid$: Observable<CommonGenericModel | undefined> = this.gridEngineFacade.selectedItemInGrid$;
  selectedItemInGrid: Signal<CommonGenericModel | undefined> = this.gridEngineFacade.selectedItemInGrid;
  abstract grids$: Observable<I4BGrid<I4BGridOptions, I4BGridData>[]>;
  abstract grids: Signal<I4BGrid<I4BGridOptions, I4BGridData>[]>;
  abstract grid$: Observable<I4BGrid<I4BGridOptions, I4BGridData> | undefined>;
  abstract grid: Signal<I4BGrid<I4BGridOptions, I4BGridData> | undefined>;
  abstract gridConfiguration: Signal<{
    sortedGridsWithoutAppDefault: I4BGrid<I4BGridOptions, I4BGridData>[];
    currentGrid: I4BGrid<I4BGridOptions, I4BGridData> | undefined;
    isGridsLoading: boolean;
  } | null>;
  abstract gridConfiguration$: Observable<{
    sortedGridsWithoutAppDefault: I4BGrid<I4BGridOptions, I4BGridData>[];
    currentGrid: I4BGrid<I4BGridOptions, I4BGridData> | undefined;
    isGridsLoading: boolean;
  } | null>;

  abstract favoriteViews: Signal<FavoriteView[]>;
  abstract favoriteViews$: Observable<FavoriteView[]>;
  abstract currentFavoriteView$: Observable<FavoriteView>;
  abstract currentFavoriteView: Signal<FavoriteView>;
  abstract favoriteViewConfiguration$: Observable<{
    sortedFavoriteViews: FavoriteView[];
    currentFavoriteView: FavoriteView | undefined;
    isFavoriteViewsLoading: boolean;
  } | null>;
  abstract favoriteViewConfiguration: Signal<{
    sortedFavoriteViews: FavoriteView[];
    currentFavoriteView: FavoriteView | undefined;
    isFavoriteViewsLoading: boolean;
  } | null>;
  abstract canCreate: Signal<boolean>;
  abstract canRead: Signal<boolean>;
  abstract canUpdate: Signal<boolean>;
  abstract canDelete: Signal<boolean>;

  protected constructor(
    protected store: Store,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected selector: ApiSelector<any, T, P, F> | ApiFeatureSelector<any, T, P, F>
  ) {
    super(store, selector);
  }

  // GRIDS
  getDefaultGrid(masterView: string) {
    this.gridEngineFacade.getDefaultGridByConcept(masterView);
  }

  loadGrids(): void {
    this.gridEngineFacade.loadGrids(this.getConcept());
  }

  // FAVORITE VIEWS
  addFavoriteView(data: { favoriteView: FavoriteView; grid?: I4BGrid<I4BGridOptions, I4BGridData> }): void {
    if (data.favoriteView.shared && !!data.grid) {
      this.favoriteViewsFacade.shareGridThenAddFavoriteViewFromMV(data.grid, data.favoriteView);
    } else {
      this.favoriteViewsFacade.addFavoriteViewFromMV(data.favoriteView);
    }
    this.gridScrollStateService.clearAll();
  }

  updateFavoriteView(data: { grid?: I4BGrid<I4BGridOptions, I4BGridData>; favoriteView: FavoriteView }): void {
    if (data.favoriteView.shared && !!data.grid) {
      this.favoriteViewsFacade.shareGridThenUpdateFavoriteViewFromMV(data.grid, data.favoriteView);
    } else {
      this.favoriteViewsFacade.updateFavoriteViewFromMV(data.favoriteView);
    }
    this.gridScrollStateService.clearAll();
  }

  deleteFavoriteView(favoriteView: FavoriteView): void {
    this.favoriteViewsFacade.deleteFavoriteView(favoriteView);
    this.gridScrollStateService.clearAll();
  }

  setFavoriteView(favoriteView: FavoriteView, masterView: string): void {
    this.favoriteViewsFacade.setCurrentFavoriteView(masterView, favoriteView);
    this.gridScrollStateService.clearAll();
  }

  getConcept(): string {
    return this.concept;
  }
}
